import React, { createContext, useContext, useEffect, useLayoutEffect, useState } from 'react'
import { ClientAnalytics } from '@/modules/clientAnalytics'

// Theme definitions - simplified to just names and display info
const themes = {
  system: {
    name: 'System',
    icon: '🖥️',
    description: 'Follow system preference'
  },
  light: {
    name: 'Light',
    icon: '☀️',
    description: 'Clean, bright interface'
  },
  dark: {
    name: 'Dark',
    icon: '🌙',
    description: 'Easy on the eyes for nightly work'
  }
} as const

export type ThemeName = keyof typeof themes
export type Theme = typeof themes[ThemeName]

interface ThemeContextType {
  currentTheme: Theme
  themeName: ThemeName
  isDark: boolean
  setTheme: (theme: ThemeName) => void
  availableThemes: ThemeName[]
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

const isSystemDark = () => {
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

interface ThemeProviderProps {
  children: React.ReactNode
  defaultTheme?: ThemeName
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children
}) => {
  const [themeName, setThemeName] = useState<ThemeName>(() => {
    if (typeof window === 'undefined') return 'system'
    const saved = window.localStorage.getItem('theme')
    if (saved === 'light' || saved === 'dark' || saved === 'system') return saved
    return 'system'
  })

  useLayoutEffect(() => {
    const root = document.documentElement
    root.className = root.className.replace(/\btheme-\S+\b/g, '').trim()
    root.classList.remove('dark')

    const pickedTheme = themeName === 'system' ? (isSystemDark() ? 'dark' : 'light') : themeName
    root.classList.add(`theme-${pickedTheme}`)
    root.style.colorScheme = pickedTheme

    // Add 'dark' class for Tailwind dark mode support
    if (pickedTheme === 'dark') {
      root.classList.add('dark')
    }
  }, [themeName])

  useEffect(() => {
    ClientAnalytics.setUserProperties({ Theme: themeName })
  }, [themeName])

  const setTheme = (theme: ThemeName) => {
    localStorage.setItem('theme', theme)
    setThemeName(theme)
  }

  const value: ThemeContextType = {
    currentTheme: themes[themeName],
    themeName,
    isDark: themeName === 'dark' || (themeName === 'system' && isSystemDark()),
    setTheme,
    availableThemes: Object.keys(themes) as ThemeName[]
  }

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  )
} 
